// +----------------------------------------------------------------------
// | 用户状态（轻量 Context，不引 Redux）
// | 提供：user / stats / 登录注册退出 / 刷新资料与统计
// +----------------------------------------------------------------------
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as authApi from '../api/auth';
import * as userApi from '../api/user';
import { getAuth, setAuth, clearAuth, updateAuthUser } from './storage';

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    const auth = getAuth();
    return auth ? auth.user : null;
  });
  const [stats, setStats] = useState(null);
  const [ready, setReady] = useState(false);

  const isLoggedIn = !!getAuth();

  /** 拉一次资料，刷新 user（失败静默，不打断页面） */
  const refreshUser = useCallback(async () => {
    const auth = getAuth();
    if (!auth) return null;
    try {
      const data = await userApi.getProfile();
      const nextUser = { ...(auth.user || {}), ...(data.user || data) };
      setUser(nextUser);
      updateAuthUser(nextUser);
      return nextUser;
    } catch (e) {
      return null;
    }
  }, []);

  /** 拉决策统计（首页「最近的决定」顶部用） */
  const refreshStats = useCallback(async () => {
    if (!getAuth()) {
      setStats(null);
      return null;
    }
    try {
      const data = await userApi.getStats();
      setStats(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const doLogin = useCallback(async ({ username, password }) => {
    const data = await authApi.login({ username, password });
    setAuth({ username: data.user.username || username, token: data.token, user: data.user });
    setUser(data.user);
    return data;
  }, []);

  const doRegister = useCallback(async ({ username, password, nickname }) => {
    const data = await authApi.register({ username, password, nickname });
    setAuth({ username: data.user.username || username, token: data.token, user: data.user });
    setUser(data.user);
    return data;
  }, []);

  const doLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // 服务端 token 可能已过期，忽略错误照常清本地
    }
    clearAuth();
    setUser(null);
    setStats(null);
  }, []);

  /** 局部更新本地 user（改完资料后调，省一次请求） */
  const patchUser = useCallback((partial) => {
    const next = updateAuthUser(partial);
    if (next) setUser(next.user);
  }, []);

  // 首屏：已登录则静默拉一次资料 + 统计
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (getAuth()) {
        await Promise.all([refreshUser(), refreshStats()]);
      }
      if (mounted) setReady(true);
    })();
    return () => {
      mounted = false;
    };
  }, [refreshUser, refreshStats]);

  const value = useMemo(
    () => ({
      user,
      stats,
      ready,
      isLoggedIn,
      doLogin,
      doRegister,
      doLogout,
      refreshUser,
      refreshStats,
      patchUser,
    }),
    [user, stats, ready, isLoggedIn, doLogin, doRegister, doLogout, refreshUser, refreshStats, patchUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser 必须在 <UserProvider> 内使用');
  }
  return ctx;
}
